/**
 * Latency probe bootstrap: M5.T13.
 *
 * Glues the M2-era `installLatencyProbe` (PerformanceObserver-based
 * input-to-paint sampler) to a tiny pub/sub surface that the on-screen
 * `<LatencyHUD>` consumes, gated on an explicit opt-in flag so it stays
 * out of regular play sessions.
 *
 * Opt-in flags (checked in order):
 *   1. `?latency=1` query string  (most ergonomic for the real-iPad
 *      profile pass: just edit the URL once on the device)
 *   2. `localStorage["drawlol:latencyProbe"] === "true"`
 *
 * When the probe is off (default), this module is effectively dead
 * weight: no PerformanceObserver subscribed, no setInterval, no HUD
 * mount.
 */

import { computeLatencyStats, installLatencyProbe, type LatencyStats } from "./latencyProbe.js";

const LS_KEY = "drawlol:latencyProbe";
const REPORT_INTERVAL_MS = 500;

export type ProbeHandle = {
  enabled: true;
  getStats: () => LatencyStats;
  reset: () => void;
  subscribe: (cb: (stats: LatencyStats) => void) => () => void;
  stop: () => void;
};

export type ProbeBootstrapResult = ProbeHandle | { enabled: false };

export function isProbeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).has("latency")) return true;
  } catch {
    /* malformed search string: ignore */
  }
  try {
    if (window.localStorage?.getItem(LS_KEY) === "true") return true;
  } catch {
    /* localStorage can throw in private mode */
  }
  return false;
}

const ZERO_STATS: LatencyStats = { sampleCount: 0, median: 0, p95: 0, max: 0 };

export function bootstrapLatencyProbe(): ProbeBootstrapResult {
  if (!isProbeEnabled()) return { enabled: false };

  const subscribers = new Set<(s: LatencyStats) => void>();
  let lastStats: LatencyStats = ZERO_STATS;
  let currentStop: () => void = () => {};

  function notify(stats: LatencyStats): void {
    lastStats = stats;
    for (const cb of subscribers) cb(stats);
  }

  function install(): void {
    const handle = installLatencyProbe({
      reportIntervalMs: REPORT_INTERVAL_MS,
      eventTypes: ["pointerdown", "pointermove", "pointerup"],
      onReport: () => {
        notify(computeLatencyStats(handle.getSamples()));
      },
    });
    currentStop = handle.stop;
  }

  install();

  return {
    enabled: true,
    getStats: () => lastStats,
    reset: () => {
      currentStop();
      install();
      notify(ZERO_STATS);
    },
    subscribe: (cb) => {
      subscribers.add(cb);
      cb(lastStats);
      return () => {
        subscribers.delete(cb);
      };
    },
    stop: () => currentStop(),
  };
}
